import {
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Box,
  Checkbox,
  TextField,
  Autocomplete,
  Grid,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Controller } from 'react-hook-form'
import SelectDamagedSection from '../../pages/DamageRegistration/SelectDamagedSection'
import PhotoPreviewRow from './PhotoPreviewRow'
import FieldNames from './fieldNames'
import {
  SectionTitle,
  SectionSubtitle1,
  SectionCaption,
} from './SectionTypography'
import { DamageDuringRouteFormState } from './index'

type Mode = 'regular' | 'emergency'

interface StepOneProps {
  control: any
  watchedForm: any
  setValue: any
  damageTypeOptions: [string, string][]
  mergeFiles: (existingFiles: FileList | null, newFiles: FileList) => FileList
  PHOTO_MIN: number
  PHOTO_MAX: number
  onDeletePhoto: (
    watchedForm: DamageDuringRouteFormState,
    photoType:
      | 'photosOwn'
      | 'photosOther'
      | 'photosObject'
      | 'photosEnvironment'
      | 'formPhoto',
    index: number
  ) => void
  registerFieldRef?: (name: string, el: HTMLElement | null) => void
  mode?: Mode
}

const StepOne = ({
  control,
  watchedForm,
  setValue,
  damageTypeOptions,
  mergeFiles,
  PHOTO_MIN,
  PHOTO_MAX,
  onDeletePhoto,
  registerFieldRef,
}: StepOneProps) => {
  const { t } = useTranslation()

  const vehicleSideOptions = [
    { label: t('vehicle_side.inside'), value: 'inside' },
    { label: t('vehicle_side.front'), value: 'front' },
    { label: t('vehicle_side.left'), value: 'left' },
    { label: t('vehicle_side.rear'), value: 'rear' },
    { label: t('vehicle_side.right'), value: 'right' },
  ]

  return (
    <>
      <Controller
        name={FieldNames.DamageLocation}
        control={control}
        defaultValue={{ own: true, other: false, object: false }}
        render={({ field, fieldState: { error } }) => (
          <FormControl
            component="fieldset"
            sx={{ width: '100%', mb: 3 }}
            error={!!error}
            data-field-name={FieldNames.DamageLocation}
            ref={(el: any) => {
              if (el && registerFieldRef)
                registerFieldRef(FieldNames.DamageLocation, el)
            }}
          >
            <SectionSubtitle1>
              {t('damage_during_route.damage_location')}
            </SectionSubtitle1>
            <SectionCaption sx={{ mb: 1 }}>
              {t('damage_during_route.multiple_answers_possible')}
            </SectionCaption>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}
            >
              <FormControlLabel
                control={<Checkbox checked={true} disabled />}
                label={t('damage_during_route.own_vehicle')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value.other}
                    onChange={(e) => {
                      const isChecked = e.target.checked
                      field.onChange({ ...field.value, other: isChecked })
                      if (!isChecked) {
                        setValue(FieldNames.LicensePlate, '')
                        setValue(FieldNames.Involved, null)
                        setValue(
                          FieldNames.PhotosOther,
                          new DataTransfer().files
                        )
                      }
                    }}
                  />
                }
                label={t('damage_during_route.other_vehicle')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value.object}
                    onChange={(e) => {
                      const isChecked = e.target.checked
                      field.onChange({ ...field.value, object: isChecked })
                      if (!isChecked) {
                        setValue(
                          FieldNames.PhotosObject,
                          new DataTransfer().files
                        )
                      }
                    }}
                  />
                }
                label={t('damage_during_route.object')}
              />
            </Box>
            {error && (
              <Typography color="error" variant="caption">
                {error.message
                  ? t(error.message)
                  : t('common.this_field_required')}
              </Typography>
            )}
          </FormControl>
        )}
      />
      {watchedForm.damageLocation.other && (
        <Controller
          name={FieldNames.Involved}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl
              component="fieldset"
              sx={{ width: '100%', mb: 3 }}
              error={!!error}
              data-field-name={FieldNames.Involved}
              ref={(el: any) => {
                if (el && registerFieldRef)
                  registerFieldRef(FieldNames.Involved, el)
              }}
            >
              <SectionSubtitle1>
                {t('damage_during_route.involved_question')}
              </SectionSubtitle1>
              <RadioGroup
                value={
                  field.value === null
                    ? 'false'
                    : field.value === true
                      ? 'true'
                      : 'false'
                }
                onChange={(e) => {
                  const newValue = e.target.value === 'true'
                  if (e.target.value === 'false') {
                    setValue(FieldNames.FormPhoto, null)
                  } else {
                    setValue(FieldNames.WhatHappened, '')
                  }
                  field.onChange(newValue)
                }}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label={t('common.yes')}
                />
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label={t('common.no')}
                />
              </RadioGroup>
              {error && (
                <Typography color="error" variant="caption">
                  {error.message
                    ? t(error.message)
                    : t('common.this_field_required')}
                </Typography>
              )}
            </FormControl>
          )}
        />
      )}
      {watchedForm.damageLocation.other && (
        <>
          <SectionTitle>{t('damage_during_route.other_vehicle')}</SectionTitle>
          <Controller
            name={FieldNames.LicensePlate}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Box
                data-field-name={FieldNames.LicensePlate}
                ref={(el: any) => {
                  if (el && registerFieldRef)
                    registerFieldRef(FieldNames.LicensePlate, el)
                }}
                sx={{ mb: 3 }}
              >
                <SectionSubtitle1>
                  {t('damage_during_route.note_license_plate')}
                </SectionSubtitle1>
                <TextField
                  {...field}
                  label={t('damage_during_route.license_plate')}
                  sx={{ width: '100%' }}
                  error={!!error}
                  helperText={error && error.message ? t(error.message) : ''}
                  InputProps={{ sx: { py: 0.5 } }}
                />
              </Box>
            )}
          />
        </>
      )}
      <SectionTitle>{t('damage_during_route.own_vehicle')}</SectionTitle>
      <Box sx={{ mb: 3 }}>
        <SectionSubtitle1 sx={{ color: 'text.primary' }}>
          {t('damage_during_route.damage_to_vehicle_section')}
        </SectionSubtitle1>
        <SectionCaption sx={{ my: 1 }}>
          {t('damage_during_route.damage_side_helper')}
        </SectionCaption>
        <Controller
          name={FieldNames.DamageSide}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Box
              data-field-name={FieldNames.DamageSide}
              ref={(el: any) => {
                if (el && registerFieldRef)
                  registerFieldRef(FieldNames.DamageSide, el)
              }}
              sx={{ mb: 2 }}
            >
              <Autocomplete
                options={vehicleSideOptions}
                getOptionLabel={(option) => option.label}
                value={
                  vehicleSideOptions.find(
                    (option) => option.value === field.value
                  ) || null
                }
                onChange={(_, newValue) => {
                  field.onChange(newValue?.value || '')
                  setValue(FieldNames.VehicleCoordinates, null)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('damage_during_route.damage_side')}
                    error={!!error}
                    helperText={error && error.message ? t(error.message) : ''}
                  />
                )}
              />
            </Box>
          )}
        />

        <Controller
          name={FieldNames.DamageClassification}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Box
              data-field-name={FieldNames.DamageClassification}
              ref={(el: any) => {
                if (el && registerFieldRef)
                  registerFieldRef(FieldNames.DamageClassification, el)
              }}
              sx={{ mb: 2 }}
            >
              <Autocomplete
                options={damageTypeOptions}
                getOptionLabel={(option) => option[0]}
                value={
                  damageTypeOptions.find(
                    (option) => option[1] === field.value
                  ) || null
                }
                onChange={(_, newValue) => field.onChange(newValue?.[1] || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('damage_during_route.damage_classification')}
                    error={!!error}
                    helperText={error && error.message ? t(error.message) : ''}
                  />
                )}
              />
            </Box>
          )}
        />
      </Box>
      <Controller
        name={FieldNames.VehicleCoordinates}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                border: '1px solid #e0e0e0',
                overflow: 'hidden',
                height: 260,
                bgcolor: '#ffffff',
              }}
            >
              <SelectDamagedSection
                coordinates={field.value}
                vehicleSide={watchedForm.damageSide}
                addNewDamage={(event: any) => {
                  const rect = event.target.getBoundingClientRect()
                  const clientX = event.clientX ?? event.pageX - window.scrollX
                  const clientY = event.clientY ?? event.pageY - window.scrollY
                  const clickX = clientX - rect.left
                  const clickY = clientY - rect.top
                  const x_percent = (clickX / rect.width) * 100
                  const y_percent = (clickY / rect.height) * 100

                  const x_clamped = Math.min(100, Math.max(0, x_percent))
                  const y_clamped = Math.min(100, Math.max(0, y_percent))

                  const coordinates: [number, number] = [x_clamped, y_clamped]
                  field.onChange(coordinates)

                  if (event && event.preventDefault) {
                    event.preventDefault()
                    event.stopPropagation()
                  }
                }}
              />
            </Box>
            {error && error.message && (
              <SectionCaption
                sx={{ mt: 1, color: 'error.main', display: 'block' }}
              >
                {t(error.message)}
              </SectionCaption>
            )}
          </Box>
        )}
      />

      {watchedForm.involved === true ? (
        <Controller
          key="form-photo-controller"
          name={FieldNames.FormPhoto}
          control={control}
          render={() => (
            <Box
              data-field-name={FieldNames.FormPhoto}
              ref={(el: any) => {
                if (el && registerFieldRef)
                  registerFieldRef(FieldNames.FormPhoto, el)
              }}
              sx={{ mb: 3 }}
            >
              <SectionSubtitle1>
                {t('damage_during_route.damage_form_fill')}
              </SectionSubtitle1>
              <SectionCaption sx={{ mb: 1 }}>
                {t('damage_during_route.damage_form_fill_description')}
              </SectionCaption>
              <PhotoPreviewRow
                maxUploadCount={1}
                files={watchedForm.formPhoto}
                onDelete={(index) =>
                  onDeletePhoto(watchedForm, FieldNames.FormPhoto, index)
                }
                onAdd={(files) =>
                  setValue(
                    FieldNames.FormPhoto,
                    mergeFiles(watchedForm.formPhoto, files),
                    { shouldValidate: true }
                  )
                }
              />
              <Controller
                name={FieldNames.FormPhoto}
                control={control}
                render={({ fieldState: { error } }) => (
                  <>
                    {error && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          color: 'error.main',
                        }}
                      >
                        {t(error.message as string)}
                      </Typography>
                    )}
                  </>
                )}
              />
            </Box>
          )}
        />
      ) : (
        <Controller
          key="what-happened-controller"
          name={FieldNames.WhatHappened}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Box sx={{ mb: 3 }}>
              <SectionSubtitle1>
                {t('damage_during_route.what_happened')}
              </SectionSubtitle1>
              <SectionCaption sx={{ mb: 1 }}>
                {t('damage_during_route.what_happened_description')}
              </SectionCaption>
              <TextField
                {...field}
                sx={{ width: '100%' }}
                error={!!error}
                helperText={error && error.message ? t(error.message) : ''}
                multiline
                rows={4}
                fullWidth
                variant="outlined"
              />
            </Box>
          )}
        />
      )}
      {(watchedForm.damageLocation.own ||
        watchedForm.damageLocation.other ||
        watchedForm.damageLocation.object) && (
        <Box sx={{ mb: 3 }}>
          <SectionSubtitle1 sx={{ mb: 2 }}>
            {t('damage_during_route.photos_section')}
          </SectionSubtitle1>
          <Grid container spacing={2}>
            {watchedForm.damageLocation.own && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('damage_during_route.photo_situation_own')}
                </Typography>
                <Controller
                  name={FieldNames.PhotosOwn}
                  control={control}
                  render={({ fieldState: { error } }) => (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          color: 'text.secondary',
                        }}
                      >
                        {t('photo_upload.min_max', {
                          min: PHOTO_MIN,
                          max: PHOTO_MAX,
                        })}
                      </Typography>
                      {error && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.5,
                            color: 'error.main',
                          }}
                        >
                          {t(error.message as string, {
                            min: PHOTO_MIN,
                            max: PHOTO_MAX,
                          })}
                        </Typography>
                      )}
                    </>
                  )}
                />
                <PhotoPreviewRow
                  files={watchedForm.photosOwn}
                  onDelete={(index) =>
                    onDeletePhoto(watchedForm, FieldNames.PhotosOwn, index)
                  }
                  onAdd={(files) =>
                    setValue(
                      FieldNames.PhotosOwn,
                      mergeFiles(watchedForm.photosOwn, files)
                    )
                  }
                />
              </Grid>
            )}

            {watchedForm.damageLocation.other && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('damage_during_route.photo_situation_other')}
                </Typography>
                <Controller
                  name={FieldNames.PhotosOther}
                  control={control}
                  render={({ fieldState: { error } }) => (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          color: 'text.secondary',
                        }}
                      >
                        {t('photo_upload.min_max', {
                          min: PHOTO_MIN,
                          max: PHOTO_MAX,
                        })}
                      </Typography>
                      {error && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.5,
                            color: 'error.main',
                          }}
                        >
                          {t(error.message as string, {
                            min: PHOTO_MIN,
                            max: PHOTO_MAX,
                          })}
                        </Typography>
                      )}
                    </>
                  )}
                />
                <PhotoPreviewRow
                  files={watchedForm.photosOther}
                  onDelete={(index) =>
                    onDeletePhoto(watchedForm, FieldNames.PhotosOther, index)
                  }
                  onAdd={(files) =>
                    setValue(
                      FieldNames.PhotosOther,
                      mergeFiles(watchedForm.photosOther, files)
                    )
                  }
                />
              </Grid>
            )}

            {watchedForm.damageLocation.object && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('damage_during_route.object_photo') ||
                    t('damage_during_route.object')}
                </Typography>
                <Controller
                  name={FieldNames.PhotosObject}
                  control={control}
                  render={({ fieldState: { error } }) => (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          color: 'text.secondary',
                        }}
                      >
                        {t('photo_upload.min_max', {
                          min: PHOTO_MIN,
                          max: PHOTO_MAX,
                        })}
                      </Typography>
                      {error && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.5,
                            color: 'error.main',
                          }}
                        >
                          {t(error.message as string, {
                            min: PHOTO_MIN,
                            max: PHOTO_MAX,
                          })}
                        </Typography>
                      )}
                    </>
                  )}
                />
                <PhotoPreviewRow
                  files={watchedForm.photosObject}
                  onDelete={(index) =>
                    onDeletePhoto(watchedForm, FieldNames.PhotosObject, index)
                  }
                  onAdd={(files) =>
                    setValue(
                      FieldNames.PhotosObject,
                      mergeFiles(watchedForm.photosObject, files)
                    )
                  }
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('damage_during_route.photo_situation_environment')}
              </Typography>
              <Controller
                name={FieldNames.PhotosEnvironment}
                control={control}
                render={({ fieldState: { error } }) => (
                  <>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        color: 'text.secondary',
                      }}
                    >
                      {t('photo_upload.min_max', {
                        min: PHOTO_MIN,
                        max: PHOTO_MAX,
                      })}
                    </Typography>
                    {error && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mt: 0.5, color: 'error.main' }}
                      >
                        {t(error.message as string, {
                          min: PHOTO_MIN,
                          max: PHOTO_MAX,
                        })}
                      </Typography>
                    )}
                  </>
                )}
              />
              <PhotoPreviewRow
                files={watchedForm.photosEnvironment}
                onDelete={(index) =>
                  onDeletePhoto(
                    watchedForm,
                    FieldNames.PhotosEnvironment,
                    index
                  )
                }
                onAdd={(files) =>
                  setValue(
                    FieldNames.PhotosEnvironment,
                    mergeFiles(watchedForm.photosEnvironment, files)
                  )
                }
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  )
}

export default StepOne
